import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { MachineStatusResponse } from '@couch/shared';
import { api, errorMessage } from '../../api/client';
import { formatBytes, OUTDATED_AGENT } from './format';

export const statusKey = (id: string) => ['machine-status', id] as const;

function Meter({ label, used, total, text }: { label: string; used: number; total: number; text: string }) {
  return (
    <div className="meter">
      <span>{label}</span>
      {/* low/high/optimum: the bar turns amber, then red, as the machine fills up. */}
      <meter min={0} max={total || 1} low={(total || 1) * 0.6} high={(total || 1) * 0.8} optimum={0} value={used} aria-label={label} />
      <span className="muted">{text}</span>
    </div>
  );
}

function MemoryWarning({ status }: { status: MachineStatusResponse['status'] }) {
  if (status.memoryPressure !== 'high') return null;
  return (
    <p role="alert" className="warning">
      Only {status.ram.availableGb.toFixed(1)} GB of memory free and {status.swap.usedGb.toFixed(1)} GB swapped out. Close big apps
      (browsers!) before generating — runs can take many times longer.
    </p>
  );
}

function UpdateButtons({ machineId, data }: { machineId: string; data: MachineStatusResponse }) {
  const queryClient = useQueryClient();
  const done = () => queryClient.invalidateQueries({ queryKey: statusKey(machineId) });
  const restart = useMutation({ mutationFn: () => api.restartMachine(machineId), onSuccess: done });
  const agent = useMutation({ mutationFn: () => api.updateAgent(machineId), onSuccess: done });
  const comfy = useMutation({ mutationFn: () => api.updateComfyui(machineId), onSuccess: done });
  const error = restart.error ?? agent.error ?? comfy.error;
  const { updates } = data;
  return (
    <div className="machine-actions">
      <button type="button" className="button-quiet" disabled={restart.isPending} onClick={() => { restart.mutate(); }}>Restart ComfyUI</button>
      {updates.comfyui.installed !== updates.comfyui.pinned && (
        <button type="button" disabled={comfy.isPending} onClick={() => { comfy.mutate(); }}>{comfy.isPending ? 'Updating ComfyUI…' : `Update ComfyUI to ${updates.comfyui.pinned}`}</button>
      )}
      {updates.agent.installed !== updates.agent.latest && (
        <button type="button" disabled={agent.isPending} onClick={() => { agent.mutate(); }}>Update agent</button>
      )}
      {agent.isSuccess && <p role="status" className="success">The agent is updating and reconnects in a few seconds.</p>}
      {error && <p role="alert" className="form-error">{errorMessage(error)}</p>}
    </div>
  );
}

export function StatusPanel({ machineId }: { machineId: string }) {
  const status = useQuery({ queryKey: statusKey(machineId), queryFn: () => api.machineStatus(machineId), refetchInterval: 15_000, retry: false });
  if (status.isPending) return <section className="panel"><p role="status">Asking the machine…</p></section>;
  if (status.isError) {
    const old = OUTDATED_AGENT.test(errorMessage(status.error));
    return (
      <section className="panel">
        <h2>Status</h2>
        <p role="alert" className={old ? 'warning' : 'form-error'}>
          {old ? 'This machine runs an old Couch agent. Run the install line from “Add machine” once more on this machine — after that updates are a button.' : errorMessage(status.error)}
        </p>
      </section>
    );
  }
  const s = status.data.status;
  return (
    <section className="panel" aria-labelledby="status-title">
      <h2 id="status-title">Status</h2>
      <p className="muted">ComfyUI {s.comfyui.version ?? '?'} · {s.comfyui.running ? 'running' : 'not running'} · agent {s.agentVersion}</p>
      <MemoryWarning status={s} />
      <Meter label="Memory in use" used={s.ram.totalGb - s.ram.availableGb} total={s.ram.totalGb} text={`${s.ram.availableGb.toFixed(1)} of ${s.ram.totalGb.toFixed(1)} GB free`} />
      <Meter label="Swap in use" used={s.swap.usedGb} total={Math.max(s.swap.totalGb, s.swap.usedGb)} text={`${s.swap.usedGb.toFixed(1)} GB`} />
      <p className="muted">Disk: {formatBytes(s.freeDiskBytes)} free</p>
      <UpdateButtons machineId={machineId} data={status.data} />
    </section>
  );
}
